// Theme control for the shadcn (.dark class) theming model. The product default
// is dark; a toggle flips to light and the choice is persisted. We roll our own
// tiny provider instead of next-themes because next-themes injects an anti-flash
// inline <script>, which the app's strict `script-src 'self'` CSP blocks — so it
// would only add a console violation without working. Applying the class in
// main.tsx before render covers the flash instead.
//
// The useTheme() shape matches what components/ui/sonner.tsx expects.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "cairn.theme";
const DEFAULT: Theme = "dark";

function readStored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT;
}

/** Reflect the theme onto <html> (shadcn keys everything off `.dark`). */
function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/** Run once at startup (main.tsx) so the first paint already has the theme —
 *  the CSP forbids an inline <script>, so this runs as the first bundle stmt. */
export function applyStoredTheme(): void {
  applyTheme(readStored());
}

interface ThemeApi {
  theme: Theme;
  /** Alias so components written against next-themes (sonner) keep working. */
  resolvedTheme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeCtx = createContext<ThemeApi | null>(null);

export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStored());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* storage unavailable — the in-memory choice still applies this session */
    }
  }, []);

  const toggle = useCallback(
    () => setTheme(theme === "dark" ? "light" : "dark"),
    [theme, setTheme],
  );

  return (
    <ThemeCtx.Provider value={{ theme, resolvedTheme: theme, setTheme, toggle }}>
      {children}
    </ThemeCtx.Provider>
  );
}
